import { useState } from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { renderStorefrontIcon } from "./storefrontIcons";
import { t } from "../../i18n";

export default function AboutPage({ apiBaseUrl }) {
  const [currency, setCurrency] = useState("USD");

  const workflowSteps = [
    t("aboutWorkflowStep1"),
    t("aboutWorkflowStep2"),
    t("aboutWorkflowStep3"),
    t("aboutWorkflowStep4")
  ];

  const teamBadges = [
    { label: t("aboutBadge1"), iconKey: "supplier-review.svg" },
    { label: t("aboutBadge2"), iconKey: "calendar-box" },
    { label: t("aboutBadge3"), iconKey: "layers" },
    { label: t("aboutBadge4"), iconKey: "export.svg" }
  ];

  const nextRoutes = [
    {
      title: t("solutionsHeroKicker"),
      body: t("solutionsNextStepBody"),
      action: t("solutionsRequestPlan"),
      to: "/solutions"
    },
    {
      title: t("navOemPrivateLabel"),
      body: t("oemIntro"),
      action: t("solutionsExploreOem"),
      to: "/custom-service"
    },
    {
      title: t("contactTitle"),
      body: t("contactIntro"),
      action: t("aboutTalkToTeam"),
      to: "/contact"
    }
  ];

  return (
    <StorefrontLayout
      apiBaseUrl={apiBaseUrl}
      currency={currency}
      onChangeCurrency={setCurrency}
    >
      <section className="subpage-hero about-hero">
        <div className="about-hero-copy">
          <div>
            <p className="hero-kicker">{t("aboutHeroKicker")}</p>
            <h2 className="serif-title">{t("aboutHeroTitle")}</h2>
            <p>
              {t("aboutHeroBody")}
            </p>
            <p className="about-location-tag">
              <strong>{t("aboutAddress")}</strong>
            </p>
          </div>

          <div className="about-metric-row">
            <div className="about-metric-chip">
              <div className="about-metric-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><line x1="3" x2="21" y1="9" y2="9"/><line x1="9" x2="9" y1="21" y2="9"/>
                </svg>
              </div>
              <span>{t("aboutMetric1")}</span>
            </div>
            <div className="about-metric-chip">
              <div className="about-metric-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
                </svg>
              </div>
              <span>{t("aboutMetric2")}</span>
            </div>
            <div className="about-metric-chip">
              <div className="about-metric-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 14a8 8 0 0 1-8 8"/><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M14 10V5a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
                </svg>
              </div>
              <span>{t("aboutMetric3")}</span>
            </div>
          </div>
        </div>

        <figure className="about-hero-visual">
          <img src="/assets/about/company2.png" alt={t("aboutOfficeImageAlt")} loading="lazy" />
          <figcaption className="about-hero-visual-badge">
            {t("aboutOfficeCaption")}
          </figcaption>
        </figure>
      </section>

      <section className="about-layout about-team-section">
        <aside className="about-side about-side-media">
          <div className="about-side-image">
            <img src="/assets/about/company3.png" alt={t("aboutTeamImageAlt")} loading="lazy" />
          </div>
        </aside>
        <article className="about-main">
          <h3 className="serif-title">{t("aboutTeamTitle")}</h3>
          <p>
            {t("aboutTeamBody")}
          </p>
          <div className="badge-row about-badge-row-neutral">
            {teamBadges.map((item) => (
              <span key={item.label}>
                <span className="about-feature-icon" aria-hidden="true">
                  {renderStorefrontIcon(item.iconKey)}
                </span>
                <span>{item.label}</span>
              </span>
            ))}
          </div>
        </article>
      </section>

      <section className="about-layout about-layout-reversed about-factory-section">
        <aside className="about-side about-side-media">
          <div className="about-side-image">
            <img src="/assets/about/factory.png" alt={t("aboutFactoryImageAlt")} loading="lazy" />
          </div>
        </aside>
        <article className="about-main">
          <h3 className="serif-title">{t("aboutFactoryPartnerTitle")}</h3>
          <p>
            {t("aboutFactoryPartnerBody")}
          </p>
          <h3 className="serif-title about-subheading">{t("aboutCustomizationTitle")}</h3>
          <p>
            {t("aboutCustomizationBody")}
          </p>
        </article>
      </section>

      <section className="about-factory-collage">
        <img className="about-factory-collage-image" src="/assets/about/factory2.png" alt={t("aboutFactoryCollageAlt")} loading="lazy" />
      </section>

      <section className="about-layout about-method-grid">
        <article className="about-main">
          <h3 className="serif-title">{t("aboutWorkflowTitle")}</h3>
          <ol className="about-workflow-list">
            {workflowSteps.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </article>

        <aside className="about-side">
          <h3 className="serif-title">{t("aboutWhyIntegratorTitle")}</h3>
          <p>{t("aboutWhyIntegratorBody1")}</p>
          <ul className="about-risk-list">
            <li>{t("aboutWhyIntegratorList1")}</li>
            <li>{t("aboutWhyIntegratorList2")}</li>
            <li>{t("aboutWhyIntegratorList3")}</li>
          </ul>
        </aside>
      </section>

      <section className="section-title">
        <h2 className="serif-title">{t("aboutPositioningTitle")}</h2>
      </section>
      <section className="subpage-panel about-panel-grid">
        <div>
          <p>{t("aboutWhyIntegratorBody2")}</p>
        </div>
        <div>
          <p>{t("aboutPositioningBody")}</p>
        </div>
      </section>

      <section className="section-title about-next-step-title">
        <h2 className="serif-title">{t("solutionsNextStepTitle")}</h2>
      </section>

      <section className="info-cards about-route-grid">
        {nextRoutes.map((item) => (
          <article key={item.to} className="about-route-card">
            <h3 className="serif-title">{item.title}</h3>
            <p>{item.body}</p>
            <Link className="button ghost-button" to={item.to}>{item.action}</Link>
          </article>
        ))}
      </section>
    </StorefrontLayout>
  );
}
